import type {
  ConversationTriageState,
  TriageTemplate,
  TriageTool,
} from '@provision/entity-types';
import {
  TriageActionConfigSnapshot,
  TriageActionVisibility,
} from 'src/graphql/entities/conversation-triage/conversation-triage.types';
import { parseJsonSafely } from 'src/utils/objects';

/**
 * Sentinel id for the display-only placeholder shown when a partner has zero
 * `ConversationTriage` rows — never a real record id, and never valid on any
 * mutation. Distinct from any UUID shape so it can't collide with a real row.
 */
export const PLATFORM_DEFAULT_PLACEHOLDER_ID = '__platform-default-placeholder__';

export function parseTriageActionConfig(
  raw: string | null | undefined,
): TriageActionConfigSnapshot {
  return parseJsonSafely<TriageActionConfigSnapshot>(raw ?? '') ?? {};
}

function snapshotForTools(
  tools: Pick<TriageTool, 'name'>[],
  visibilityFor: (toolName: string) => string,
): TriageActionConfigSnapshot {
  const snapshot: TriageActionConfigSnapshot = {};
  tools.forEach((tool) => {
    snapshot[tool.name] = { visibility: visibilityFor(tool.name) };
  });
  return snapshot;
}

/**
 * Seeds an explicit visibility entry for every tool in `tools`, demoting
 * Auto-apply to Suggest and keeping Not-allowed/Suggest as-is. Missing tools
 * (never touched in `source`) get the template default. Used on create/clone
 * so a fully Not-allowed record round-trips instead of being read back as an
 * unset `{}` snapshot and silently replaced with platform defaults.
 */
export function cloneTriageActionConfigSnapshot(
  source: TriageActionConfigSnapshot,
  tools: Pick<TriageTool, 'name'>[],
  defaultVisibility: string,
): TriageActionConfigSnapshot {
  return snapshotForTools(tools, (toolName) => {
    const currentVisibility = source[toolName]?.visibility;
    return (currentVisibility as TriageActionVisibility | undefined) ===
      TriageActionVisibility.AutoApply
      ? TriageActionVisibility.Show
      : (currentVisibility ?? defaultVisibility);
  });
}

/**
 * A complete per-tool snapshot at the template default — every tool set to
 * `defaultVisibility`, regardless of any existing record. Used to seed a
 * brand-new configuration ("+ New configuration"), as opposed to
 * `cloneTriageActionConfigSnapshot`, which carries a source record's modes
 * forward (used by Duplicate).
 */
export function defaultTriageActionConfigSnapshot(
  tools: Pick<TriageTool, 'name'>[],
  defaultVisibility: string,
): TriageActionConfigSnapshot {
  return snapshotForTools(tools, () => defaultVisibility);
}

/**
 * Synthetic, display-only entry representing "what the runtime would
 * actually use" for a partner with zero `ConversationTriage` rows — the
 * platform default, including partner `0` action modes. It is never persisted
 * and never a valid mutation target (see `PLATFORM_DEFAULT_PLACEHOLDER_ID`);
 * the first edit lazily creates the partner's real row via the existing
 * `isFirstCreate` flow, and a refetch replaces this placeholder with that
 * record.
 */
export function buildPlatformDefaultPlaceholder(
  template: TriageTemplate,
): ConversationTriageState {
  const platformModes = parseTriageActionConfig(template.actionConfig);
  return {
    conversationTriageId: PLATFORM_DEFAULT_PLACEHOLDER_ID,
    name: null,
    isDefault: true,
    instructions: template.instructions,
    actionConfig: JSON.stringify(
      snapshotForTools(
        template.tools,
        (toolName) => platformModes[toolName]?.visibility ?? template.defaultVisibility,
      ),
    ),
    llmConfig: template.llmConfig,
    usedByBehaviors: [],
  };
}

const TOOL_MODE_SUMMARY_LABELS: Record<string, string> = {
  [TriageActionVisibility.AutoApply]: 'auto-apply',
  [TriageActionVisibility.Show]: 'suggest',
  [TriageActionVisibility.Hide]: 'off',
};

const TOOL_MODE_SUMMARY_ORDER = [
  TriageActionVisibility.AutoApply,
  TriageActionVisibility.Show,
  TriageActionVisibility.Hide,
];

/** e.g. "2 auto-apply · 3 suggest · 1 off" for the selector option subline. */
export function summarizeTriageToolModes(
  actionConfig: string | null | undefined,
  tools: Pick<TriageTool, 'name'>[],
  defaultVisibility: string,
): string {
  const snapshot = parseTriageActionConfig(actionConfig);
  const counts = new Map<string, number>();
  tools.forEach((tool) => {
    const visibility = snapshot[tool.name]?.visibility ?? defaultVisibility;
    counts.set(visibility, (counts.get(visibility) ?? 0) + 1);
  });
  return TOOL_MODE_SUMMARY_ORDER.map((visibility) => {
    const count = counts.get(visibility) ?? 0;
    return count > 0 ? `${count} ${TOOL_MODE_SUMMARY_LABELS[visibility]}` : null;
  })
    .filter((entry): entry is string => Boolean(entry))
    .join(' · ');
}
